import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { api, post } from "../../utils/api";
import { useNavigate } from "react-router-dom";
import LoginSlider from "./LoginSlider";
import companyLogo from "../../assets/images/companyLogo.png";
import vajraPoweredByLogo from "../../assets/images/vajra_powered_by_logo.svg";
import { getSubdomainLabel } from "../../utils/consultantDomain";
import { jwtDecode } from "jwt-decode";
import styles from "../../styles/common/unauthorized.module.css";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EastIcon from "@mui/icons-material/East";

const ResetNewPAssword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState([]);

  const [passwordErrors, setPasswordErrors] = useState([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [isTokenVerified, setIsTokenVerified] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await post("/api/auth/validateResetToken", { token });
        if (res?.data?.statusCode === 1) {
          setPasswordSet(res?.data?.resetStatus);
        } else {
          setSnackOpen({
            open: true,
            message: res.data.message || "An error occurred",
            severity: "error",
          });
        }
      } catch (error) {
        if (error?.status === 409) {
          setPasswordSet(true);
        } else if (error?.status === 404) {
          setSnackOpen({
            open: true,
            message: error?.response?.data?.message || "An error occurred",
            severity: "error",
          });
          setPasswordSet(false);
        } else if (error?.status === 401) {
          setPasswordSet(false);
        }
      } finally {
        setIsTokenVerified(true);
      }
    };

    if (token) verifyToken();
  }, [token]);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const validatePassword = (password) => {
    const validationErrors = [];
    if (password.length < 8)
      validationErrors.push("Password must be at least 8 characters long.");
    if (!/[A-Z]/.test(password))
      validationErrors.push(
        "Password must contain at least 1 uppercase letter."
      );
    if (!/[a-z]/.test(password))
      validationErrors.push(
        "Password must contain at least 1 lowercase letter."
      );
    if (!/[0-9!@#$%^&*]/.test(password))
      validationErrors.push(
        "Password must contain at least 1 number or special character."
      );
    return validationErrors;
  };

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
    setPasswordErrors([]);
  };

  const handlePasswordBlur = () => {
    setPasswordErrors(validatePassword(newPassword));
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setConfirmPasswordError("");
  };

  const handleConfirmPasswordBlur = () => {
    if (confirmPassword && confirmPassword !== newPassword) {
      setConfirmPasswordError("Both passwords should be identical.");
    }
  };

  const handleSubmit = async () => {
    if (newPassword !== confirmPassword) {
      setErrors(["Both passwords should be identical."]);
      return;
    }

    if (passwordErrors.length === 0 && confirmPasswordError === "") {
      try {
        const response = await post(
          `${api}/api/auth/reset/password?token=${token}`,
          { password: newPassword }
        );

        // console.log(response.data.message);
        setSnackOpen({
          open: true,
          message: response.data.message,
          severity: "success",
        });
        navigate("/login");
      } catch (error) {
        console.error("Error resetting password", error);
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    }
  };

  if (!isTokenVerified) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontFamily: "Inter",
          fontSize: "16px",
        }}
      >
        Validating token...
      </div>
    );
  }

  return (
    <>
      {!passwordSet ? (
        <div
          style={{
            width: "100%",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className={styles.card}>
            <WarningAmberIcon
              sx={{
                width: 50,
                height: 50,
                color: "rgb(215, 60, 60)",
                marginBottom: "0.5rem",
              }}
            />

            <p className={styles.card_title}>
              Your Password Reset Link Has Been Expired
            </p>

            <p className={styles.card_description}>
              For security reasons, the password reset link is valid for only 15
              minutes. Your link has expired.
            </p>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#1C486B",
                color: "#ededed",
                borderColor: "#1C486B",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#123A55",
                },
              }}
              endIcon={<EastIcon />}
              onClick={() => {
                window.location.href = "/login";
              }}
            >
              Contiune to login
            </Button>
          </div>
        </div>
      ) : (
        <div
          style={{
            height: "100vh",
            width: "100%",
            margin: "0",
            padding: "0",
            display: "flex",

            overflow: "hidden",
          }}
        >
          {/* Left Section - Image (remains same) */}
          <Grid
            item
            sx={{
              flex: 1,
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "left",
              justifyContent: "left",
              width: "62.15",
              height: "100vh",
              padding: 0,
              margin: 0,
            }}
          >
            <LoginSlider />
          </Grid>

          {/* Right Section - Toggle between Login and OTP */}
          <Grid
            item
            sx={{
              display: "flex",
              padding: "60px 40px 40px 40px",
              justifyContent: "center",
              width: "33.33%",
              bgcolor: "rgba(255, 255, 255, 1)",
              position: "relative",
              // backgroundColor: "rgb(196, 230, 240)",
              overflow: "auto",
            }}
          >
            <Box sx={{ width: "100%" }}>
              <>
                <p className="main-heading">Reset Password</p>
                <p className="subheading">Please Use your Registered Email</p>

                <p className="input-label">Create New Password</p>
                <TextField
                  fullWidth
                  placeholder="New Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  error={errors.length > 0}
                  sx={{ mb: 1, marginTop: "-0.9rem" }}
                  InputProps={{
                    sx: { height: "45px" },
                    autoComplete: "off",
                    form: {
                      autocomplete: "off",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                          // Ensure the button gets focus correctly
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onFocus={() => {
                    // This ensures that clicking the field shows the icon initially
                    if (!showPassword) setShowPassword(true);
                  }}
                />

                {passwordErrors.length > 0 && (
                  <Box
                    sx={{
                      color: "#D32F2F",
                      fontSize: "13px",
                      fontFamily: "Inter",
                      fontWeight: 400,
                      marginLeft: "-0.5rem",
                      marginTop: "-0.3rem",
                    }}
                  >
                    {passwordErrors.map((error, index) => (
                      <helperText key={index}>- {error}</helperText>
                    ))}
                  </Box>
                )}

                <ul
                  style={{
                    color: "#45464F",
                    fontSize: "13px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    paddingLeft: "1rem",
                  }}
                >
                  <li>Password must be at least 8 characters long.</li>
                  <li>Password must contain at least 1 Uppercase</li>
                  <li>Password must contain at least 1 Lowercase</li>
                  <li>
                    Password must contain at least 1 No. or Special Character
                  </li>
                </ul>

                <p className="input-label">Confirm New Password</p>
                <TextField
                  fullWidth
                  placeholder="Confirm New Password"
                  variant="outlined"
                  // type={showPassword ? "password" : "password"}
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  onBlur={handleConfirmPasswordBlur}
                  error={!!confirmPasswordError}
                  helperText={
                    <p
                      style={{
                        fontFamily: "Inter",
                        color: "#D32F2F",
                        fontSize: "13px",
                        marginTop: "-0.3rem",
                        fontWeight: 400,
                        marginLeft: "-0.7rem",
                      }}
                    >
                      {confirmPasswordError}
                    </p>
                  }
                  sx={{ mb: 1, marginTop: "-0.9rem" }}
                  InputProps={{
                    sx: { height: "45px" },
                    autoComplete: "off",
                    endAdornment: (
                      <InputAdornment position="end"></InputAdornment>
                    ),
                  }}
                  // onFocus={() => {
                  //   // No need to toggle visibility as the icon is removed
                  //   if (!showPassword) setShowPassword(true);
                  // }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: "#EB7E0E",
                    color: "#fff",
                    textTransform: "none",
                    py: 1.2,
                    marginTop: "40px",
                    borderRadius: "10px",
                    "&:hover": {
                      bgcolor: "#EB7E0E",
                    },
                  }}
                  disabled={
                    passwordErrors.length > 0 ||
                    !!confirmPasswordError ||
                    !newPassword ||
                    !confirmPassword
                  }
                >
                  Confirm New Password
                </Button>

                <Snackbar
                  open={snackOpen.open}
                  message={snackOpen.message}
                  autoHideDuration={3000}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                  onClose={() => setSnackOpen({ ...snackOpen, open: false })}
                >
                  <Alert
                    severity={snackOpen.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                  >
                    {snackOpen.message}
                  </Alert>
                </Snackbar>
              </>

              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  position: "absolute",
                  bottom: "20px",
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  color: "#838383",
                  fontSize: "11px",
                  ml: 0, // Remove the left margin
                }}
              >
                Powered by
                <span style={{ marginLeft: "0.25rem" }}>
                  <img
                    src={getSubdomainLabel() ? vajraPoweredByLogo : companyLogo}
                    alt="Company Logo"
                    style={{
                      height: "25px",
                      display: "inline-block",
                      marginTop: "-5px",
                    }}
                  />
                </span>
              </Typography>
            </Box>
          </Grid>
        </div>
      )}
    </>
  );
};

export default ResetNewPAssword;
